/**
 * Consumer Guarantees Page (Fiops Borrower Portal redesign)
 *
 * Flat PageTemplate(variant="list") with a 3-tile KPI strip (Total exposure /
 * Active guarantees / Expiring (12mo)) + tabs (Active obligations / History)
 * + custom row layout via `GuaranteeRow`. Read-only — no right CTA.
 *
 * Spec: `/tmp/design-pass/borrower/specs/reference-section.md` § Guarantees.
 * The legacy `EntityHubTemplate` hub chrome was replaced with the same flat
 * list pattern used by Watchlist.
 *
 * @see Issue #1199 - Consumer Guarantor Hub (original)
 */

import ExposureIcon from '@mui/icons-material/AttachMoney';
import HistoryIcon from '@mui/icons-material/History';
import ScheduleIcon from '@mui/icons-material/Schedule';
import GuarantorIcon from '@mui/icons-material/Shield';
import {
  Box,
  EmptyState,
  formatCurrencyWhole,
  GlassCard,
  GlassTabs,
  ShimmerLoader,
  Stack,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { GuarantorStatus } from '@ops-matrix/shared-types';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GuaranteeRow, type GuaranteeRowData } from '../../components/guarantors/GuaranteeRow';
import { PageTemplate } from '../../components/layout/PageTemplate';
import { LoanKpiTile } from '../../components/loans/LoanKpiTile';
import { useConsumerAuth } from '../../contexts/ConsumerAuthContext';
import { useMyGuarantees, useMyGuarantorExposure } from '../../lib/trpc/hooks';

// ============================================================================
// Constants
// ============================================================================

/**
 * Window for the "Expiring (12mo)" KPI. Wider than the legacy 90-day
 * EXPIRING_SOON_DAYS heuristic — borrowers want long-horizon planning, not
 * just "what's burning next quarter".
 */
const EXPIRING_KPI_WINDOW_DAYS = 365;

// ============================================================================
// Helpers
// ============================================================================

function getDaysUntilExpiration(expirationDate: string | null | undefined): number | null {
  if (!expirationDate) return null;
  const diff = new Date(expirationDate).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function formatCurrencyCompact(amount: number | null | undefined): string {
  if (amount == null || amount === 0) return '—';
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(amount >= 10_000_000 ? 0 : 1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(amount >= 10_000 ? 0 : 1)}K`;
  return formatCurrencyWhole(amount);
}

// ============================================================================
// Tab Components
// ============================================================================

interface RowListProps {
  guarantees: GuaranteeRowData[];
  emptyTitleKey: string;
  emptyDescriptionKey: string;
  emptyIcon: typeof GuarantorIcon;
  onRowClick: (caseId: string) => void;
}

function GuaranteeList({
  guarantees,
  emptyTitleKey,
  emptyDescriptionKey,
  emptyIcon: EmptyIcon,
  onRowClick,
}: RowListProps) {
  const { t } = useI18n();
  if (guarantees.length === 0) {
    return <EmptyState icon={<EmptyIcon />} title={t(emptyTitleKey)} description={t(emptyDescriptionKey)} />;
  }
  return (
    <>
      {/* Sticky column header row — matches GuaranteeRow's 6-col grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr 100px',
          gap: 1.5,
          px: 1.5,
          py: 1,
          bgcolor: 'action.hover',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: 'text.secondary' }}>
          {t('consumer.guarantees.col.borrower', { defaultValue: 'Borrower' })}
        </Typography>
        <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: 'text.secondary' }}>
          {t('consumer.guarantees.col.loan', { defaultValue: 'Loan' })}
        </Typography>
        <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: 'text.secondary' }}>
          {t('consumer.guarantees.col.amount', { defaultValue: 'Amount' })}
        </Typography>
        <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: 'text.secondary' }}>
          {t('consumer.guarantees.col.type', { defaultValue: 'Type' })}
        </Typography>
        <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: 'text.secondary' }}>
          {t('consumer.guarantees.col.expiry', { defaultValue: 'Expiry' })}
        </Typography>
        <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: 'text.secondary' }}>
          {t('consumer.guarantees.col.status', { defaultValue: 'Status' })}
        </Typography>
      </Box>
      <Stack divider={<Box sx={{ height: 1, bgcolor: 'divider', mx: 1.5 }} />}>
        {guarantees.map((g) => (
          <GuaranteeRow key={g.id} guarantee={g} onClick={onRowClick} />
        ))}
      </Stack>
    </>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function GuarantorHubPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { consumer } = useConsumerAuth();

  const consumerId = consumer?.id ?? undefined;
  const { data: guaranteesData, isPending: guaranteesLoading } = useMyGuarantees(
    consumerId ? { consumerId } : undefined,
  );
  const { data: exposureData } = useMyGuarantorExposure(consumerId ? { consumerId } : undefined);

  const guarantees = useMemo<GuaranteeRowData[]>(
    () =>
      ((guaranteesData?.data ?? []) as GuaranteeRowData[]).map((g) => ({
        id: g.id,
        caseId: g.caseId,
        caseNumber: g.caseNumber,
        guaranteeType: g.guaranteeType,
        guaranteeAmount: g.guaranteeAmount,
        status: g.status,
        expirationDate: g.expirationDate,
        borrowerName: g.borrowerName,
      })),
    [guaranteesData?.data],
  );

  const activeGuarantees = useMemo(
    () => guarantees.filter((g) => g.status === GuarantorStatus.ACTIVE || g.status === GuarantorStatus.PENDING),
    [guarantees],
  );

  const historicalGuarantees = useMemo(
    () => guarantees.filter((g) => g.status === GuarantorStatus.RELEASED || g.status === GuarantorStatus.DEFAULTED),
    [guarantees],
  );

  // KPI calculations
  const totalExposure = exposureData?.totalGuaranteeAmount ?? 0;
  const activeCount = activeGuarantees.length;
  const expiringInWindow = useMemo(
    () =>
      guarantees.filter((g) => {
        if (g.status !== GuarantorStatus.ACTIVE) return false;
        const days = getDaysUntilExpiration(g.expirationDate);
        return days !== null && days > 0 && days <= EXPIRING_KPI_WINDOW_DAYS;
      }).length,
    [guarantees],
  );

  // Tabs
  const [activeTab, setActiveTab] = useState<'active' | 'history'>('active');
  const handleTabChange = useCallback((_e: React.SyntheticEvent, val: string) => {
    setActiveTab(val as 'active' | 'history');
  }, []);

  const handleRowClick = (caseId: string) => {
    if (caseId) navigate(`/applications/${caseId}`);
  };

  return (
    <PageTemplate
      variant="list"
      titleKey="consumer.guarantees.pageTitle"
      descriptionKey="consumer.guarantees.pageSubtitle"
      icon={GuarantorIcon}
    >
      {/* KPI strip — 3 tiles */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(3, 1fr)' },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <LoanKpiTile
          label={t('consumer.guarantees.kpiTotalExposure')}
          value={formatCurrencyCompact(totalExposure)}
          tone="id"
          icon={ExposureIcon}
          loading={guaranteesLoading}
        />
        <LoanKpiTile
          label={t('consumer.guarantees.kpiActiveGuarantees')}
          value={activeCount}
          tone="orange"
          icon={GuarantorIcon}
          loading={guaranteesLoading}
        />
        <LoanKpiTile
          label={t('consumer.guarantees.kpiExpiring')}
          value={expiringInWindow}
          tone="amber"
          icon={ScheduleIcon}
          loading={guaranteesLoading}
        />
      </Box>

      {/* Tabs + list panel */}
      <GlassCard sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <GlassTabs
            value={activeTab}
            onChange={handleTabChange}
            tabs={[
              {
                value: 'active',
                label:
                  activeCount > 0
                    ? `${t('consumer.guarantees.tabActive')} (${activeCount})`
                    : t('consumer.guarantees.tabActive'),
              },
              { value: 'history', label: t('consumer.guarantees.tabHistory') },
            ]}
            size="small"
          />
        </Box>

        {guaranteesLoading ? (
          <Box sx={{ p: 2 }}>
            <ShimmerLoader variant="rectangular" height={240} />
          </Box>
        ) : activeTab === 'active' ? (
          <GuaranteeList
            guarantees={activeGuarantees}
            emptyTitleKey="consumer.guarantees.emptyActiveTitle"
            emptyDescriptionKey="consumer.guarantees.emptyActiveSub"
            emptyIcon={GuarantorIcon}
            onRowClick={handleRowClick}
          />
        ) : (
          <GuaranteeList
            guarantees={historicalGuarantees}
            emptyTitleKey="consumer.guarantees.emptyHistoryTitle"
            emptyDescriptionKey="consumer.guarantees.emptyHistorySub"
            emptyIcon={HistoryIcon}
            onRowClick={handleRowClick}
          />
        )}
      </GlassCard>
    </PageTemplate>
  );
}
